import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, CardHeader, CardBody } from '@/components/common/Card';
import Badge from '@/components/common/Badge';
import Button from '@/components/common/Button';
import { getPortalCommissions } from '@/services/affiliateApi';

export default function AffiliatePortalCommissions() {
  const [page, setPage] = useState(1);

  const { data, isLoading } = useQuery({
    queryKey: ['portal-commissions', page],
    queryFn: () => getPortalCommissions(page, 20),
  });

  const commissions = (data as any)?.commissions || [];
  const total = (data as any)?.total || 0;
  const totalPages = Math.ceil(total / 20);

  // Calculate summary
  const totalEarned = commissions.reduce((sum: number, c: any) => sum + (c.commission_amount_cents || 0), 0);
  const pendingAmount = commissions
    .filter((c: any) => c.status === 'pending')
    .reduce((sum: number, c: any) => sum + (c.commission_amount_cents || 0), 0);

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold text-gray-900">Commissions</h1>

      <div className="grid grid-cols-3 gap-4">
        <Card><CardBody>
          <p className="text-sm text-gray-500">Total Earned (This Page)</p>
          <p className="text-xl font-bold text-green-600">${(totalEarned / 100).toFixed(2)}</p>
        </CardBody></Card>
        <Card><CardBody>
          <p className="text-sm text-gray-500">Pending Balance</p>
          <p className="text-xl font-bold text-orange-600">${(pendingAmount / 100).toFixed(2)}</p>
        </CardBody></Card>
        <Card><CardBody>
          <p className="text-sm text-gray-500">Total Records</p>
          <p className="text-xl font-bold text-blue-600">{total}</p>
        </CardBody></Card>
      </div>

      <Card>
        <CardBody>
          {isLoading ? (
            <div className="text-gray-500 text-center py-8">Loading...</div>
          ) : !commissions.length ? (
            <div className="text-gray-500 text-center py-8">No commissions yet</div>
          ) : (
            <>
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Base Amount</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Rate</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Commission</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Month</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {commissions.map((c: any) => (
                    <tr key={c.id}>
                      <td className="px-4 py-3 text-sm">${(c.base_amount_cents / 100).toFixed(2)}</td>
                      <td className="px-4 py-3 text-sm">{(c.commission_rate * 100).toFixed(0)}%</td>
                      <td className="px-4 py-3 text-sm font-medium text-green-600">${(c.commission_amount_cents / 100).toFixed(2)}</td>
                      <td className="px-4 py-3 text-sm">{c.month_number ?? 'Annual'}</td>
                      <td className="px-4 py-3 text-sm">
                        <Badge variant={c.status === 'paid' ? 'success' : c.status === 'pending' ? 'warning' : 'secondary'}>
                          {c.status}
                        </Badge>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500">{new Date(c.created_at).toLocaleDateString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {totalPages > 1 && (
                <div className="flex items-center justify-between mt-4 px-4">
                  <span className="text-sm text-gray-500">Page {page} of {totalPages}</span>
                  <div className="flex gap-2">
                    <Button size="sm" variant="secondary" disabled={page <= 1} onClick={() => setPage(page - 1)}>Previous</Button>
                    <Button size="sm" variant="secondary" disabled={page >= totalPages} onClick={() => setPage(page + 1)}>Next</Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardBody>
      </Card>
    </div>
  );
}
